import { useEffect, useState, useCallback } from "react";
import {
  Box, Typography, Paper, TextField, Grid, Table, TableHead,
  TableRow, TableCell, TableBody, CircularProgress, Alert,
  MenuItem, Select, FormControl, InputLabel, Chip, Collapse,
  IconButton, Divider, Stack
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import FilterListIcon from "@mui/icons-material/FilterList";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import TwoWheelerIcon from "@mui/icons-material/TwoWheeler";

import {profitLossService} from "../service/profitLossService";
const LOCATIONS = ["ALL","Chaitanyapur", "Mahisadal"];

const MONTHS = [
  { value: "1",  label: "January"   },
  { value: "2",  label: "February"  },
  { value: "3",  label: "March"     },
  { value: "4",  label: "April"     },
  { value: "5",  label: "May"       },
  { value: "6",  label: "June"      },
  { value: "7",  label: "July"      },
  { value: "8",  label: "August"    },
  { value: "9",  label: "September" },
  { value: "10", label: "October"   },
  { value: "11", label: "November"  },
  { value: "12", label: "December"  },
];

const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 5 }, (_, i) => String(currentYear - i));

// ── Stat card ────────────────────────────────────────────────────────────────
const StatCard = ({ icon, label, value, color = "#1976d2" }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2.5,
      border: "1px solid",
      borderColor: "divider",
      borderRadius: 2,
      display: "flex",
      alignItems: "center",
      gap: 2,
      flex: 1,
      minWidth: 180
    }}
  >
    <Box
      sx={{
        width: 44,
        height: 44,
        borderRadius: 2,
        bgcolor: `${color}18`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color,
        flexShrink: 0
      }}
    >
      {icon}
    </Box>
    <Box>
      <Typography variant="caption" color="text.secondary" fontWeight={500} display="block">
        {label}
      </Typography>
      <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
        {value}
      </Typography>
    </Box>
  </Paper>
);

// ── Location block ───────────────────────────────────────────────────────────
const LocationBlock = ({ group }) => {
  const [open, setOpen] = useState(true);

  return (
    <Paper
      elevation={0}
      sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, overflow: "hidden", mb: 2 }}
    >
      {/* Header row */}
      <Box
        sx={{
          px: 2.5,
          py: 1.5,
          bgcolor: "grey.50",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: "pointer",
          "&:hover": { bgcolor: "grey.100" },
          transition: "background .15s"
        }}
        onClick={() => setOpen((o) => !o)}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <LocalShippingIcon fontSize="small" sx={{ color: "primary.main" }} />
          <Typography fontWeight={700} fontSize={15}>
            {group.location}
          </Typography>
          <Chip
            label={`${group.totalTransfers} transfers`}
            size="small"
            sx={{ bgcolor: "primary.50", color: "primary.dark", fontWeight: 600, fontSize: 11 }}
          />
        </Stack>

        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography fontWeight={700} color="success.dark" fontSize={15}>
            ₹{group.totalAmount.toLocaleString("en-IN")}
          </Typography>
          <IconButton size="small">
            {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
        </Stack>
      </Box>

      <Collapse in={open}>
        <Divider />
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: "grey.50" }}>
              {["Challan No", "Date", "From", "Model", "Engine", "Chasis", "Colour", "SKU", "Cost"].map((h) => (
                <TableCell key={h} sx={{ fontWeight: 700, fontSize: 12, whiteSpace: "nowrap" }}>
                  {h}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {group.transfers.map((t, i) => (
              <TableRow
                key={i}
                sx={{ "&:hover": { bgcolor: "primary.50" }, transition: "background .1s" }}
              >
                <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>
                  {t.challanNo || "—"}
                </TableCell>
                <TableCell sx={{ fontSize: 12, whiteSpace: "nowrap" }}>
                  {t.date ? new Date(t.date).toLocaleDateString("en-IN", {
                    day: "2-digit", month: "short", year: "numeric"
                  }) : "—"}
                </TableCell>
                <TableCell>
                  <Chip
                    label={t.fromLocation || "—"}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: 11 }}
                  />
                </TableCell>
                <TableCell sx={{ fontSize: 12 }}>{t.model || "—"}</TableCell>
                <TableCell sx={{ fontSize: 12, fontFamily: "monospace" }}>{t.engine || "—"}</TableCell>
                <TableCell sx={{ fontSize: 12, fontFamily: "monospace" }}>{t.chasis || "—"}</TableCell>
                <TableCell sx={{ fontSize: 12 }}>{t.colour || "—"}</TableCell>
                <TableCell sx={{ fontSize: 12 }}>{t.skuCode || "—"}</TableCell>
                <TableCell sx={{ fontSize: 12, fontWeight: 600, color: "success.dark" }}>
                  {t.transferCost != null ? `₹${Number(t.transferCost).toLocaleString("en-IN")}` : "—"}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Collapse>
    </Paper>
  );
};

// ── Main component ───────────────────────────────────────────────────────────
const TransferCostAnalytics = () => {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState("");

  // filters
  const [location, setLocation] = useState("ALL");
  const [filterMode, setFilterMode] = useState("none"); // none | dateRange | monthYear | year
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate]     = useState("");
  const [month, setMonth]       = useState("");
  const [year, setYear]         = useState("");

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const params = {};
      if (location && location !== "ALL") params.location = location;

      if (filterMode === "dateRange" && fromDate && toDate) {
        params.fromDate = fromDate;
        params.toDate   = toDate;
      } else if (filterMode === "monthYear" && month && year) {
        params.month = month;
        params.year  = year;
      } else if (filterMode === "year" && year) {
        params.year = year;
      }

      const res = await profitLossService.transferCostService(params);
      // console.log('nnuwehw ankur ',res.data)
      setData(res);
      console.log(res)
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load transfer data");
    } finally {
      setLoading(false);
    }
  }, [location, filterMode, fromDate, toDate, month, year]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Page title */}
      <Stack direction="row" alignItems="center" spacing={1.5} mb={3}>
        <LocalShippingIcon color="primary" />
        <Typography variant="h5" fontWeight={700}>
          Transfer Cost Analytics
        </Typography>
      </Stack>

      {/* ── Filters ── */}
      <Paper
        elevation={0}
        sx={{ p: 2.5, mb: 3, border: "1px solid", borderColor: "divider", borderRadius: 2 }}
      >
        <Stack direction="row" alignItems="center" spacing={1} mb={2}>
          <FilterListIcon fontSize="small" color="action" />
          <Typography fontWeight={600} fontSize={13} color="text.secondary">
            FILTERS
          </Typography>
        </Stack>

        <Grid container spacing={2} alignItems="flex-end">
          {/* Location */}
          <Grid item xs={12} sm={6} md={2.5}>
            <FormControl fullWidth size="small">
              <InputLabel>Location</InputLabel>
              <Select value={location} label="Location" onChange={(e) => setLocation(e.target.value)}>
                {LOCATIONS.map((l) => (
                  <MenuItem key={l} value={l}>{l}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Filter mode */}
          <Grid item xs={12} sm={6} md={2.5}>
            <FormControl fullWidth size="small">
              <InputLabel>Filter by</InputLabel>
              <Select value={filterMode} label="Filter by" onChange={(e) => { setFilterMode(e.target.value); setFromDate(""); setToDate(""); setMonth(""); setYear(""); }}>
                <MenuItem value="none">All Time</MenuItem>
                <MenuItem value="dateRange">Date Range</MenuItem>
                <MenuItem value="monthYear">Month & Year</MenuItem>
                <MenuItem value="year">Year Only</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Conditional date inputs */}
          {filterMode === "dateRange" && (
            <>
              <Grid item xs={12} sm={6} md={2.5}>
                <TextField
                  label="From Date" type="date" size="small" fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={fromDate} onChange={(e) => setFromDate(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2.5}>
                <TextField
                  label="To Date" type="date" size="small" fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={toDate} onChange={(e) => setToDate(e.target.value)}
                />
              </Grid>
            </>
          )}

          {filterMode === "monthYear" && (
            <>
              <Grid item xs={12} sm={6} md={2.5}>
                <FormControl fullWidth size="small">
                  <InputLabel>Month</InputLabel>
                  <Select value={month} label="Month" onChange={(e) => setMonth(e.target.value)}>
                    {MONTHS.map((m) => (
                      <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2.5}>
                <FormControl fullWidth size="small">
                  <InputLabel>Year</InputLabel>
                  <Select value={year} label="Year" onChange={(e) => setYear(e.target.value)}>
                    {YEARS.map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
            </>
          )}

          {filterMode === "year" && (
            <Grid item xs={12} sm={6} md={2.5}>
              <FormControl fullWidth size="small">
                <InputLabel>Year</InputLabel>
                <Select value={year} label="Year" onChange={(e) => setYear(e.target.value)}>
                  {YEARS.map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* ── Loading / Error ── */}
      {loading && (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      )}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* ── Summary cards ── */}
      {!loading && data && (
        <>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} mb={3} flexWrap="wrap">
            <StatCard
              icon={<LocalShippingIcon />}
              label="Total Transfers"
              value={data.grandTotalTransfers ?? 0}
              color="#1976d2"
            />
            <StatCard
              icon={<CurrencyRupeeIcon />}
              label="Grand Total Amount"
              value={`₹${(data.grandTotalAmount ?? 0).toLocaleString("en-IN")}`}
              color="#2e7d32"
            />
            <StatCard
              icon={<TwoWheelerIcon />}
              label="Locations"
              value={data.byLocation?.length ?? 0}
              color="#ed6c02"
            />
          </Stack>

          {/* ── Location blocks ── */}
          {data.byLocation?.length === 0 && (
            <Alert severity="info">No transfer records found for the selected filters.</Alert>
          )}

          {data.byLocation?.map((group) => (
            <LocationBlock key={group.location} group={group} />
          ))}
        </>
      )}
    </Box>
  );
};

export default TransferCostAnalytics;